import DataLoader from 'dataloader'
import { collection } from '@/infra/collection'
import { DocumentType } from '@/features/docs/DocumentType'
import { type DocsApi } from '@/features/docs/api/docs-api'
import { mapDocumentTypeResourceToProps } from '@/features/docs/document-mappers'
import { Id } from '@/infra/id'

/**
 * Manages document types.
 */
export class DocumentTypeStore {
  private readonly _loader = new DataLoader(this._fetchTypesCore.bind(this))

  readonly types = collection({
    getDataId: (m) => m.id,
    getModelId: (m) => m.id,
    create: () => new DocumentType(),
  })

  constructor(private readonly _api: DocsApi) {}

  /**
   * Lists document types.
   */
  async listTypes(signal?: AbortSignal): Promise<DocumentType[]> {
    const response = await this._api.types.list(
      {
        // We're only looking for client-uploader types.
        uploader: 'CLIENT',
        limit: 1_000,
      },
      signal,
    )

    return this.types.set(response.data.map(mapDocumentTypeResourceToProps))
  }

  /**
   * Get a document type by ID.
   * @param id
   */
  async fetchDocumentType(id: Id): Promise<DocumentType | null> {
    return Id.some(id) ? this._loader.load(Id.assert(id)) : null
  }

  /**
   * Fetches document types by ID.
   */
  private async _fetchTypesCore(
    ids: readonly string[],
  ): Promise<(DocumentType | null)[]> {
    const resources = await this._api.types.getMany(ids)
    return this.types.set(
      resources.map((r) => (r ? mapDocumentTypeResourceToProps(r) : null)),
    )
  }
}
